import React, { useContext, useState } from 'react'
import styled from 'styled-components'
import { Tabs } from '@seiue/ui'
import { QuestionTypeEnum } from 'packages/sdks-next/growp'
import { deleteSubQuestions, getSubQuestions, getTreeNodeId } from '../utils'
import { EditProductQuestionProps } from '../types'
import { EditSubType } from './EditProductItem/EditSubType'
import { EditProductAnswer } from './EditProductAnswer'
import { EditSubStem } from './EditProductItem/EditSubStem'
import { EditSubScore } from './EditProductItem/EditSubScore'
import { EditSubAnalyze } from './EditProductItem/EditSubAnalyze'
import { Form } from 'antd'
import { AnyType } from 'packages/types'
import { JudgeMode } from '@/features/test-paper/types'
import { EditPaperContext } from '@/features/test-paper/pages/EditPaper/context'

/**
 * 试卷答案 - 复合题型编辑与创建
 * @returns React.ReactNode
 */
export const EditProductQuestion: React.FC<EditProductQuestionProps> = (
  props
) => {
  const { paper, limitLevel } = useContext(EditPaperContext)
  const { mainQuestion, subQuestions, setSubQuestions } = props
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined)

  const productSubQuestions = getSubQuestions(
    subQuestions,
    mainQuestion?.id || mainQuestion?.treeNodeId || 0
  )
  const items = productSubQuestions.map((question, index) => {
    const commonProps = {
      ...props,
      treeNodeId: question.treeNodeId,
      curQuestion: question,
    }
    return {
      key: `${question.treeNodeId}`,
      label: `小题${index + 1}`,
      closable: limitLevel > 0 ? false : productSubQuestions.length > 1,
      children: (
        <div
          style={{
            padding: '16px 10px 10px 0',
          }}
        >
          <EditSubType {...commonProps} />
          <EditSubStem {...commonProps} />
          <Form.Item
            label="答案"
            required
          >
            <EditProductAnswer
              type={question.type}
              {...commonProps}
            />
          </Form.Item>
          <EditSubAnalyze {...commonProps} />
          {paper.showScore ? <EditSubScore {...commonProps} /> : null}
        </div>
      ),
    }
  })

  const onEdit = (key: string, action: 'add' | 'remove') => {
    if (action === 'remove') {
      // 删除子题时要一同删除子题的子题
      const newSubQuestions = deleteSubQuestions(subQuestions, Number(key))
      setSubQuestions(newSubQuestions)
      const firstLevelSubQuestions = getSubQuestions(
        newSubQuestions,
        mainQuestion?.id || mainQuestion?.treeNodeId || 0
      )
      if (firstLevelSubQuestions.length > 1) {
        setActiveKey(`${firstLevelSubQuestions[0].treeNodeId}`)
      }
    }
    if (action === 'add') {
      const treeNodeId = getTreeNodeId(
        mainQuestion ? [mainQuestion, ...subQuestions] : subQuestions
      )
      setSubQuestions([
        ...subQuestions,
        {
          type: QuestionTypeEnum.SingleChoice,
          options: ['A', 'B', 'C', 'D'],
          standardAnswers: [],
          parentId: mainQuestion?.id || mainQuestion?.treeNodeId,
          treeNodePid: mainQuestion?.id || mainQuestion?.treeNodeId,
          treeNodeId: treeNodeId,
          weight: treeNodeId,
          settings: {
            judgeMode: JudgeMode.Auto,
          },
        } as AnyType,
      ])
      setActiveKey(`${treeNodeId}`)
    }
  }

  return (
    <Wrapper>
      <Tabs
        hideAdd={limitLevel > 0}
        type="editable-card"
        activeKey={activeKey}
        onChange={(key) => setActiveKey(key)}
        items={items}
        onEdit={(e, action) => {
          onEdit(e as string, action)
        }}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  border-radius: 8px;
  padding: 10px 20px 4px 20px;
  box-shadow: 0px 0px 1px 0px rgba(21, 29, 41, 0.1),
    0px 0px 4px 0px rgba(21, 29, 41, 0.02),
    0px 4px 16px 0px rgba(21, 29, 41, 0.06);
  &&& {
    .ant-tabs-nav-list {
      padding-top: 10px;
    }
    .ant-tabs-tab {
      min-width: 85px;
      height: 32px;
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      background-color: ${(props) => props.theme.background._3};
    }
    .ant-tabs-tab-active {
      background-color: transparent;
    }
    .ant-tabs-nav-add {
      min-width: 54px;
      height: 32px;
      padding: 0 0;
      border: 1px solid transparent;
      background-color: transparent;
    }
    .ant-tabs-tab-active .ant-tabs-tab-btn,
    .ant-tabs-tab-btn {
      font-weight: 400;
      font-size: 14px;
      color: ${(props) => props.theme.brand._1};
    }
    .ant-tabs-tab-btn {
      color: ${(props) => props.theme.text._2};
    }
    .ant-tabs-tabpane {
      border: none;
    }
  }
`
